import React, { useState } from 'react';
import { FiUpload, FiCheckCircle, FiAlertCircle, FiTrendingUp, FiEdit3, FiFileText, FiAward } from 'react-icons/fi';
import styles from '../styles/AIGrader.module.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';

const AIGrader = () => {
  const [assignment, setAssignment] = useState(null);
  const [rubric, setRubric] = useState('');
  const [grading, setGrading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAssignment(file);
      setResult(null); // Clear previous results
    }
  };

  const handleGrade = async () => {
    if (!assignment) {
      alert('Please upload an assignment first');
      return;
    }

    setGrading(true);
    setUploadProgress(0);

    try {
      // Upload assignment file
      const formData = new FormData();
      formData.append('file', assignment);
      
      const uploadResponse = await fetch(`${API_BASE}/api/process_document`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload assignment');
      }

      const uploadData = await uploadResponse.json();
      setUploadProgress(50);

      // Grade the assignment
      const gradeResponse = await fetch(`${API_BASE}/api/grade_assignment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: uploadData.text,
          rubric: rubric || 'General academic essay rubric',
          filename: assignment.name
        })
      });

      if (!gradeResponse.ok) {
        throw new Error('Failed to grade assignment');
      }

      const gradeData = await gradeResponse.json();
      setResult(gradeData);
      setUploadProgress(100);

    } catch (error) {
      console.error('Grading error:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGrading(false);
    }
  };

  const getGradeColor = (grade) => {
    const numericGrade = parseFloat(grade);
    if (numericGrade >= 90) return '#22c55e'; // Green (A)
    if (numericGrade >= 80) return '#3b82f6'; // Blue (B)
    if (numericGrade >= 70) return '#f59e0b'; // Orange (C)
    if (numericGrade >= 60) return '#ef4444'; // Red (D)
    return '#991b1b'; // Dark red (F)
  };

  return (
    <div className={styles.graderContainer}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <FiAward size={32} className={styles.headerIcon} />
          <div>
            <h1>AI Assignment Grader</h1>
            <p>Get instant, detailed feedback on your work before submission</p>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Upload Section */}
        <div className={styles.uploadSection}>
          <div className={styles.card}>
            <h2>
              <FiFileText size={20} />
              Upload Your Assignment
            </h2>
            
            <div className={styles.uploadArea}>
              <input
                type="file"
                id="assignment-upload"
                accept=".pdf,.docx,.txt,.md"
                onChange={handleFileUpload}
                className={styles.fileInput}
              />
              <label htmlFor="assignment-upload" className={styles.uploadLabel}>
                <FiUpload size={32} />
                <span>Click to upload or drag and drop</span>
                <span className={styles.uploadHint}>PDF, DOCX, TXT, or MD (Max 10MB)</span>
              </label>
            </div>

            {assignment && (
              <div className={styles.fileInfo}>
                <FiCheckCircle color="#22c55e" />
                <span>{assignment.name}</span>
                <span className={styles.fileSize}>
                  ({(assignment.size / 1024).toFixed(1)} KB)
                </span>
              </div>
            )}
          </div>

          <div className={styles.card}>
            <h2>
              <FiEdit3 size={20} />
              Rubric or Requirements (Optional)
            </h2>
            <textarea
              value={rubric}
              onChange={(e) => setRubric(e.target.value)}
              placeholder="Paste your assignment rubric or specific requirements here...&#10;&#10;Example:&#10;- Thesis statement (20%)&#10;- Evidence and analysis (30%)&#10;- Organization (20%)&#10;- Grammar and style (20%)&#10;- Citations (10%)&#10;&#10;If left blank, we'll use a general academic rubric."
              className={styles.rubricInput}
              rows={8}
            />
          </div>

          <button
            onClick={handleGrade}
            disabled={!assignment || grading}
            className={styles.gradeBtn}
          >
            {grading ? (
              <>
                <div className={styles.spinner} />
                Grading... ({uploadProgress}%)
              </>
            ) : (
              <>
                <FiAward size={18} />
                Grade My Assignment
              </>
            )}
          </button>
        </div>

        {/* Results Section */}
        {result && (
          <div className={styles.resultsSection}>
            {/* Overall Grade */}
            <div className={styles.gradeCard} style={{ borderColor: getGradeColor(result.overall_grade) }}>
              <div className={styles.gradeHeader}>
                <h2>Overall Grade</h2>
                <div className={styles.gradeDisplay} style={{ color: getGradeColor(result.overall_grade) }}>
                  {result.letter_grade}
                  <span className={styles.numericGrade}>{result.overall_grade}%</span>
                </div>
              </div>
              <p className={styles.gradeSummary}>{result.summary}</p>
            </div>

            {/* Strengths */}
            <div className={styles.feedbackCard}>
              <h3>
                <FiCheckCircle color="#22c55e" size={20} />
                Strengths
              </h3>
              <ul className={styles.feedbackList}>
                {result.strengths.map((strength, idx) => (
                  <li key={idx} className={styles.strengthItem}>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas for Improvement */}
            <div className={styles.feedbackCard}>
              <h3>
                <FiTrendingUp color="#f59e0b" size={20} />
                Areas for Improvement
              </h3>
              <ul className={styles.feedbackList}>
                {result.improvements.map((improvement, idx) => (
                  <li key={idx} className={styles.improvementItem}>
                    <strong>{improvement.category}:</strong> {improvement.feedback}
                    {improvement.suggestion && (
                      <div className={styles.suggestion}>
                        💡 <em>{improvement.suggestion}</em>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Detailed Breakdown */}
            <div className={styles.breakdownCard}>
              <h3>
                <FiFileText size={20} />
                Detailed Breakdown
              </h3>
              <div className={styles.criteriaList}>
                {result.criteria_breakdown.map((criterion, idx) => (
                  <div key={idx} className={styles.criterionItem}>
                    <div className={styles.criterionHeader}>
                      <span className={styles.criterionName}>{criterion.name}</span>
                      <span className={styles.criterionScore} style={{ 
                        color: getGradeColor((criterion.score / criterion.max_score) * 100) 
                      }}>
                        {criterion.score}/{criterion.max_score}
                      </span>
                    </div>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill}
                        style={{ 
                          width: `${(criterion.score / criterion.max_score) * 100}%`,
                          backgroundColor: getGradeColor((criterion.score / criterion.max_score) * 100)
                        }}
                      />
                    </div>
                    <p className={styles.criterionFeedback}>{criterion.feedback}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Specific Issues */}
            {result.specific_issues && result.specific_issues.length > 0 && (
              <div className={styles.issuesCard}>
                <h3>
                  <FiAlertCircle color="#ef4444" size={20} />
                  Specific Issues Found
                </h3>
                <div className={styles.issuesList}>
                  {result.specific_issues.map((issue, idx) => (
                    <div key={idx} className={styles.issueItem}>
                      <div className={styles.issueType}>{issue.type}</div>
                      <p className={styles.issueDescription}>{issue.description}</p>
                      {issue.location && (
                        <span className={styles.issueLocation}>📍 {issue.location}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Plan */}
            <div className={styles.actionCard}>
              <h3>
                <FiTrendingUp color="#3b82f6" size={20} />
                Action Plan to Improve
              </h3>
              <ol className={styles.actionList}>
                {result.action_plan.map((action, idx) => (
                  <li key={idx} className={styles.actionItem}>
                    <strong>Step {idx + 1}:</strong> {action}
                  </li>
                ))}
              </ol>
              <div className={styles.potentialGrade}>
                <FiAward size={18} />
                <span>
                  Potential grade after improvements: <strong>{result.potential_grade}</strong>
                </span>
              </div>
            </div>

            {/* Re-grade Button */}
            <button
              onClick={() => {
                setResult(null);
                setAssignment(null);
              }}
              className={styles.regradeBtn}
            >
              <FiUpload size={16} />
              Upload Revised Version
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIGrader;
